import { signal } from "@preact/signals";

/**
 * The content loader's state — infinite scroll, filtering, layout, selection mode.
 *
 * This lived as **18 hidden `<input>` elements** copy-pasted across seven server
 * views (`Front`, `Artist`, `Collection`, `List`, `Vip`, `Bestsellers`,
 * `Dashboard`), read and written at ~64 sites across `ui/contentLoader.ts` and
 * five feature modules. Every value was a string: booleans were `"1"`/`"0"`,
 * numbers were parsed on each read, and `lm_target` held **another element's id**
 * — so `features/search.ts` dereferenced the DOM twice to find where to render.
 *
 * A signal rather than a plain module object (the shape `globalState.ts` uses):
 * components can subscribe, so the parts of the UI that mirror loader state stop
 * needing anyone to push updates into them by id.
 *
 * ## Where the values come from
 *
 * The server-initialised half arrives as one typed island payload (see
 * `packages/shared/src/loader.ts`); the client-mutated half starts from those and
 * changes as the user scrolls, filters and selects. Both live here, because
 * splitting them was what made the DOM version confusing: nothing marked which
 * inputs the server owned.
 */
export interface LoaderState {
  // ── server-initialised ──────────────────────────────────────────
  /** What the loader fetches: "images" everywhere today. */
  mode: string;
  /** Element id the results are rendered into (was `lm_target`, an id-in-a-value). */
  targetId: string;
  /** Base path used for pushState as the user pages (was `lm_url`). */
  url: string;
  /** JSON filter object the server seeded (was `lm_filters`, a JSON string). */
  filters: Record<string, string | number>;
  /** Extra option string passed through to the search procedure. */
  options: string;
  /** Rows per fetch. */
  qty: number;
  layout: string;
  filtersOpen: boolean;
  infiniteScrollEnabled: boolean;
  infiniteScrollDiscreet: boolean;
  /** Filters used once infinite scroll passes the end of the current content. */
  infiniteScrollFilters: Record<string, string | number> | null;
  /** Clear existing results before the next load (`lm_autoclear`, front/vip only). */
  autoClear: boolean;

  // ── client-mutated ──────────────────────────────────────────────
  /** Paging cursor. */
  from: number;
  /** More to fetch. Was `lm_active`, `"1"`/`"0"`. */
  hasMore: boolean;
  /** Selection mode is on (`lm_select`). */
  selecting: boolean;
  /** Infinite scroll has taken over past the collection's own content. */
  infiniteScrollActive: boolean;
  /** Ids rendered so far, used to avoid duplicates (`lm_artworks_loaded`, a CSV). */
  loadedArtworkIds: number[];
}

const EMPTY: LoaderState = {
  mode: "",
  targetId: "",
  url: "",
  filters: {},
  options: "",
  qty: 100,
  layout: "bigthumbs",
  filtersOpen: false,
  infiniteScrollEnabled: false,
  infiniteScrollDiscreet: false,
  infiniteScrollFilters: null,
  autoClear: false,
  from: 0,
  hasMore: false,
  selecting: false,
  infiniteScrollActive: false,
  loadedArtworkIds: [],
};

/**
 * `null` on pages with no loader — most of the site. Readers must handle it;
 * the DOM version expressed the same thing as "the hidden input isn't there",
 * which is exactly the check that silently returned `undefined` instead.
 */
export const loaderState = signal<LoaderState | null>(null);

/** Seed from the server island. Call once, during boot. */
export function initLoaderState(initial: Partial<LoaderState>): void {
  loaderState.value = { ...EMPTY, ...initial };
}

/**
 * Apply a partial update.
 *
 * A no-op when there is no loader on the page, mirroring the old behaviour where
 * writing to a missing hidden input did nothing.
 */
export function updateLoader(patch: Partial<LoaderState>): void {
  const current = loaderState.value;
  if (!current) return;
  loaderState.value = { ...current, ...patch };
}

/** Read helper for the common `if (loader) …` guard. */
export function getLoader(): LoaderState | null {
  return loaderState.value;
}
